import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from "react";

export type IUseControlledValueProps<T> = {
  controlledValue?: T;
  controlledOnChange?: Dispatch<SetStateAction<T>>;
  defaultValue?: T;
};

export const useControlledValue = <T>({
  controlledValue,
  controlledOnChange,
  defaultValue,
}: IUseControlledValueProps<T>) => {
  const [uncontrolledValue, setUncontrolledValue] = useState<T | undefined>(
    defaultValue,
  );

  const uncontrolledOnChange = useCallback<Dispatch<SetStateAction<T>>>(
    (value) => {
      setUncontrolledValue((prevValue) => {
        if (typeof value === "function") {
          const updater = value as (prevState: T) => T;
          return prevValue !== undefined ? updater(prevValue) : defaultValue;
        }
        return value;
      });
    },
    [defaultValue],
  );

  const value =
    controlledValue !== undefined ? controlledValue : uncontrolledValue;

  const onChange =
    controlledOnChange !== undefined
      ? controlledOnChange
      : uncontrolledOnChange;

  return {
    value,
    onChange,
  };
};
